import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { RefreshCw, AlertTriangle, CheckCircle, X } from 'lucide-react';
import api from '../utils/api';

const fmtNum = (n) => Number(n || 0).toLocaleString('it-IT');
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`);

export const RestoreModal = ({ target, onClose }) => {
  const [st, setSt] = useState({ phase: 'starting' });
  const [word, setWord] = useState('');
  const [confirming, setConfirming] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!target) return;
    let stopped = false;
    const poll = async () => {
      try {
        const r = await api.get('/backup/restore/status');
        if (!stopped) setSt(r.data);
      } catch { /* ignora errori di polling */ }
    };
    (async () => {
      try {
        const r = await api.post('/backup/restore/preview', { source: target.source, name: target.name });
        if (!r.data.started) { setSt({ phase: 'error', error: r.data.error }); return; }
        pollRef.current = setInterval(poll, 2000);
        poll();
      } catch (e) {
        setSt({ phase: 'error', error: e.response?.data?.detail || e.message });
      }
    })();
    return () => { stopped = true; if (pollRef.current) clearInterval(pollRef.current); };
  }, [target]);

  if (!target) return null;

  const busy = ['starting', 'preview_running', 'safety_backup', 'restoring'].includes(st.phase);
  const close = async () => {
    if (busy && st.phase !== 'preview_running' && st.phase !== 'starting') return;
    try { await api.post('/backup/restore/cancel'); } catch { /* ok */ }
    onClose();
  };
  const confirm = async () => {
    setConfirming(true);
    try {
      const r = await api.post('/backup/restore/confirm', { confirm: word });
      if (!r.data.started) setSt(s => ({ ...s, phase: 'error', error: r.data.error }));
    } catch (e) {
      setSt(s => ({ ...s, phase: 'error', error: e.response?.data?.detail || e.message }));
    } finally { setConfirming(false); }
  };

  const p = st.preview;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="restore-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">⚠️ Ripristino database da backup</h3>
            <p className="text-xs text-gray-500 font-mono mt-1">{target.name}</p>
          </div>
          {!busy && (
            <button onClick={close} className="text-gray-400 hover:text-gray-600" data-testid="restore-close-x"><X className="h-5 w-5" /></button>
          )}
        </div>

        {(st.phase === 'starting' || st.phase === 'preview_running') && (
          <div className="py-6 text-center space-y-3">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-600" />
            <p className="text-sm text-gray-600" data-testid="restore-preview-detail">{st.detail || 'Preparazione anteprima…'}</p>
            <div className="h-2 bg-blue-100 rounded-full overflow-hidden max-w-sm mx-auto">
              <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${st.percent || 0}%` }} />
            </div>
          </div>
        )}

        {st.phase === 'preview_ready' && p && (
          <>
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900">
              Il ripristino <b>sostituisce</b> il contenuto delle collezioni elencate con quello del backup
              del <b>{p.backup_timestamp ? new Date(p.backup_timestamp).toLocaleString('it-IT') : '—'}</b>:
              tutto ciò che è stato inserito DOPO quella data andrà perso.
              Prima di iniziare viene creato automaticamente un <b>backup di sicurezza</b> dello stato attuale.
            </div>
            <div className="flex gap-2 flex-wrap text-xs">
              <span className="px-2.5 py-1 rounded-full bg-slate-100 font-medium">{fmtSize(p.file_size)}</span>
              <span className="px-2.5 py-1 rounded-full bg-slate-100 font-medium">{p.collections.length} collezioni</span>
              <span className="px-2.5 py-1 rounded-full bg-slate-100 font-medium">{fmtNum(p.total_docs)} documenti nel backup</span>
            </div>
            <div className="border rounded-lg overflow-y-auto max-h-64">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0"><tr className="text-xs text-slate-500">
                  <th className="text-left p-2">Collezione</th>
                  <th className="text-right p-2">Nel backup</th>
                  <th className="text-right p-2">Attuali</th>
                  <th className="text-right p-2">Differenza</th>
                </tr></thead>
                <tbody className="divide-y">
                  {p.collections.map(c => {
                    const diff = c.backup_docs - c.current_docs;
                    return (
                      <tr key={c.name} data-testid={`restore-coll-${c.name}`}>
                        <td className="p-2 font-mono text-xs">{c.name}</td>
                        <td className="p-2 text-right">{fmtNum(c.backup_docs)}</td>
                        <td className="p-2 text-right text-slate-500">{fmtNum(c.current_docs)}</td>
                        <td className={`p-2 text-right text-xs font-medium ${diff === 0 ? 'text-slate-400' : diff > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {diff > 0 ? `+${fmtNum(diff)}` : fmtNum(diff)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-700">Per confermare scrivi <b>RIPRISTINA</b>:</p>
              <div className="flex gap-2">
                <input value={word} onChange={e => setWord(e.target.value)} placeholder="RIPRISTINA"
                  className="border rounded-lg px-3 py-2 text-sm flex-1" data-testid="restore-confirm-input" />
                <Button onClick={confirm} disabled={word.trim().toUpperCase() !== 'RIPRISTINA' || confirming}
                  className="bg-red-600 hover:bg-red-700 text-white" data-testid="restore-confirm-btn">
                  <AlertTriangle className="h-4 w-4 mr-1" /> Ripristina ora
                </Button>
                <Button variant="outline" onClick={close} data-testid="restore-cancel-btn">Annulla</Button>
              </div>
            </div>
          </>
        )}

        {(st.phase === 'safety_backup' || st.phase === 'restoring') && (
          <div className="py-6 text-center space-y-3">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-red-600" />
            <p className="text-sm font-semibold text-gray-800">
              {st.phase === 'safety_backup' ? '1/2 — Backup di sicurezza dello stato attuale…' : '2/2 — Ripristino in corso…'}
            </p>
            <p className="text-xs text-gray-500" data-testid="restore-progress-detail">{st.detail}</p>
            <div className="h-2.5 bg-red-100 rounded-full overflow-hidden max-w-sm mx-auto">
              <div className="h-full bg-red-600 rounded-full transition-all" style={{ width: `${st.percent || 0}%` }} />
            </div>
            <p className="text-[11px] text-gray-400">Non chiudere questa pagina.</p>
          </div>
        )}

        {st.phase === 'done' && (
          <div className="py-4 text-center space-y-3" data-testid="restore-done">
            <CheckCircle className="h-10 w-10 mx-auto text-emerald-600" />
            <p className="font-semibold text-gray-900">Ripristino completato ✅</p>
            <p className="text-sm text-gray-600">
              {st.result?.restored_collections} collezioni · {fmtNum(st.result?.restored_docs)} documenti ripristinati
            </p>
            <p className="text-xs text-gray-500">Backup di sicurezza pre-ripristino: <span className="font-mono">{st.result?.safety_backup}</span></p>
            <Button onClick={async () => { await close(); window.location.reload(); }} data-testid="restore-done-close">Chiudi e ricarica</Button>
          </div>
        )}

        {st.phase === 'error' && (
          <div className="space-y-3">
            <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-sm text-red-800" data-testid="restore-error">
              ❌ {st.error || 'Errore sconosciuto'}
            </div>
            <div className="text-right"><Button variant="outline" onClick={close}>Chiudi</Button></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RestoreModal;
